import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X, ArrowRight, Sparkles, Loader2, Calendar, Settings, HelpCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ROICalculator from "@/components/ROICalculator";
import PaymentBadges from "@/components/PaymentBadges";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";

const FAQ_ITEMS = [
  {
    q: "What do I get with the monthly subscription?",
    a: "Your subscription gives you access to premium CV templates, cover letter tools, ATS-friendly formatting support, AI-powered line-by-line rewrites, unlimited evaluations, and other career resources available on ResumeLab.",
  },
  {
    q: "Can I cancel my subscription at any time?",
    a: "Yes. You can cancel your monthly subscription at any time through your account dashboard. Your access will remain active until the end of your current billing period unless otherwise stated in your subscription terms.",
  },
  {
    q: "How will I be charged?",
    a: "You will be charged automatically every month using your selected payment method unless you cancel before your next billing date.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "ResumeLab accepts M-Pesa, Visa, and Mastercard for monthly subscription payments.",
  },
  {
    q: "What happens if my payment fails?",
    a: "If your payment is unsuccessful, we may notify you and ask you to update your payment method. Access to premium features may be paused if payment is not completed within the required time.",
  },
  {
    q: "How do I contact support?",
    a: "If you need help with your subscription, billing, or account access, please contact the ResumeLab support team at hello@resumelab.co.ke or through the contact details provided on the website.",
  },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-zinc-200/70" data-testid={`pricing-faq-item-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left hover:text-[#EF4444] transition-colors"
        aria-expanded={open}
        data-testid={`pricing-faq-toggle-${idx}`}
      >
        <span className="font-display font-semibold text-base sm:text-lg text-zinc-900">{q}</span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="pb-5 pr-8 text-sm leading-relaxed text-zinc-600" data-testid={`pricing-faq-answer-${idx}`}>
          {a}
        </p>
      )}
    </div>
  );
}

const FEATURES = [
  { label: "Resume evaluations / month", free: "1", pro: "Unlimited" },
  { label: "ATS scoring", free: true, pro: true },
  { label: "5-dimension breakdown", free: true, pro: true },
  { label: "Line-by-line improvements", free: true, pro: true },
  { label: "Skill gap analysis", free: true, pro: true },
  { label: "AI rewrites", free: false, pro: true },
  { label: "Cover letter generator", free: false, pro: true },
  { label: "PDF export of audits", free: false, pro: true },
  { label: "Audit history & trends", free: true, pro: true },
];

function FeatureRow({ label, free, pro }) {
  const cell = (v) =>
    typeof v === "boolean" ? (
      v ? <Check className="w-4 h-4 text-[#007A5E]" /> : <X className="w-4 h-4 text-zinc-300" />
    ) : (
      <span className="font-mono text-sm tabular-nums">{v}</span>
    );
  return (
    <div className="grid grid-cols-12 border-b border-zinc-200/70 items-center">
      <div className="col-span-6 p-4 text-sm">{label}</div>
      <div className="col-span-3 p-4 flex justify-center">{cell(free)}</div>
      <div className="col-span-3 p-4 flex justify-center">{cell(pro)}</div>
    </div>
  );
}

export default function Pricing() {
  const { user, loading: authLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => { refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/subscription", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout", { description: e?.response?.data?.detail || e.message });
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not open billing portal", { description: e?.response?.data?.detail || e.message });
      setPortalLoading(false);
    }
  };

  const subActive = user?.subscription_status && ["active", "trialing"].includes(user.subscription_status);
  const subCanceledAtEnd = subActive && user?.cancel_at_period_end;
  const renewLabel = user?.current_period_end
    ? new Date(user.current_period_end).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 text-zinc-900" data-testid="pricing-page">
      <SEO
        title="Pricing — $20/month ResumeLab Pro"
        description="Free first audit, $20/month for unlimited AI resume rewrites, ATS scoring, and cover letter generator. Cancel anytime. Calculate your ROI."
        path="/pricing"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8 lg:py-12">
        <div className="overline mb-4">◆ {t("nav.pricing").toUpperCase()}</div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.98] max-w-3xl">
          {t("pricing.title")}
        </h1>
        <p className="text-zinc-600 text-base lg:text-lg mt-5 max-w-xl">
          {t("pricing.subtitle")}
        </p>

        {user?.is_pro && (
          <div className="mt-12 border-2 border-[#EF4444] bg-blue-50/40 p-6 lg:p-8" data-testid="pro-active-banner">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="overline mb-2 text-[#EF4444]">◆ YOUR PRO ACCESS</div>
                <div className="font-display font-extrabold text-2xl mb-1" data-testid="pro-active-status">
                  ResumeLab Pro · {subActive ? "Active" : "Active (legacy)"}
                </div>
                <div className="text-sm text-zinc-600 inline-flex items-center gap-2 mt-1">
                  <Calendar className="w-4 h-4" />
                  {subCanceledAtEnd && renewLabel ? (
                    <>Cancels on <span className="font-mono">{renewLabel}</span></>
                  ) : subActive && renewLabel ? (
                    <>Renews on <span className="font-mono">{renewLabel}</span></>
                  ) : user?.pro_until ? (
                    <>Expires on <span className="font-mono">{new Date(user.pro_until).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span></>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                {user?.has_stripe_customer && (
                  <button
                    onClick={openPortal}
                    disabled={portalLoading}
                    data-testid="manage-subscription-cta"
                    className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white disabled:opacity-50"
                  >
                    {portalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings className="w-4 h-4" /> Manage subscription</>}
                  </button>
                )}
                <Link to="/evaluate" className="btn-sharp bg-[#EF4444] hover:bg-[#002FA7] text-white">
                  Run an audit <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">          {/* FREE */}
          <div className="bg-white p-8 lg:p-10">
            <div className="overline mb-4">FREE</div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="font-display font-extrabold text-6xl">$0</span>
              <span className="text-zinc-500">/ forever</span>
            </div>
            <p className="text-zinc-500 mb-8">For trying out ResumeLab on your current resume.</p>
            <Link to="/evaluate" data-testid="free-cta" className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white w-full">
              Run a free audit
            </Link>
            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> 1 evaluation per month</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Full scoring + ATS analysis</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Line-by-line improvements</li>
              <li className="flex items-start gap-2"><X className="w-4 h-4 text-zinc-300 mt-0.5" /> AI rewrites</li>
              <li className="flex items-start gap-2"><X className="w-4 h-4 text-zinc-300 mt-0.5" /> Cover letter generator</li>
              <li className="flex items-start gap-2"><X className="w-4 h-4 text-zinc-300 mt-0.5" /> PDF export</li>
            </ul>
          </div>

          {/* PRO */}
          <div className="bg-zinc-900 text-white p-8 lg:p-10 relative">
            <div className="absolute top-6 right-6 px-2 py-1 bg-[#EF4444] text-white text-[10px] font-mono tracking-widest uppercase">
              POPULAR
            </div>
            <div className="overline text-zinc-400 mb-4">PRO · MONTHLY</div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="font-display font-extrabold text-6xl">$20</span>
              <span className="text-zinc-400">/ month</span>
            </div>
            <p className="text-zinc-400 mb-8">For active job seekers shipping multiple applications a week. Auto-renews monthly. Cancel anytime from the billing portal.</p>
            <button
              onClick={subscribe}
              disabled={loading || authLoading || subActive}
              data-testid="upgrade-cta"
              className="btn-sharp bg-white text-zinc-900 hover:bg-[#EF4444] hover:text-white w-full disabled:opacity-50"
            >
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Starting checkout...</>) :
               subActive ? (<>Pro active <Sparkles className="w-4 h-4" /></>) :
               (<>Start Pro — $20 / month <ArrowRight className="w-4 h-4" /></>)}
            </button>
            <p className="mt-3 text-xs text-zinc-500">
              Secure checkout via Stripe · Cancel anytime · By subscribing you agree to our{" "}
              <Link to="/terms" className="underline hover:text-white">Terms</Link> &amp;{" "}
              <Link to="/privacy" className="underline hover:text-white">Privacy</Link>.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Unlimited resume evaluations</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> AI rewrites for every section</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Cover letter generator (per JD)</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> PDF export of every audit</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Score trends across resumes</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#007A5E] mt-0.5" /> Priority AI processing</li>
            </ul>
          </div>
        </div>

        {/* Payment methods trust bar */}
        <div className="mt-6 flex justify-center">
          <div className="bg-white border border-zinc-200 px-6 py-4 inline-flex">
            <PaymentBadges
              methods={["visa", "mastercard", "amex", "mpesa", "applepay", "googlepay"]}
              align="center"
              size="md"
              label="Secure checkout · All major payment methods"
            />
          </div>
        </div>

        {/* Compare */}
        <div className="mt-20">
          <div className="overline mb-6">◇ COMPARE</div>
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 border-b-2 border-zinc-900 bg-zinc-50">
              <div className="col-span-6 p-4 overline">Feature</div>
              <div className="col-span-3 p-4 overline text-center">Free</div>
              <div className="col-span-3 p-4 overline text-center text-[#EF4444]">Pro</div>
            </div>
            {FEATURES.map((f) => <FeatureRow key={f.label} {...f} />)}
          </div>
        </div>

        <ROICalculator />

        {/* FAQ */}
        <section className="mt-20" data-testid="pricing-faq">
          <div className="flex items-center gap-2 mb-6">
            <HelpCircle className="w-4 h-4 text-zinc-500" />
            <div className="overline">◈ FREQUENTLY ASKED QUESTIONS</div>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 mb-2">
            Subscription, billing & cancellation
          </h2>
          <p className="text-zinc-600 text-sm mb-8 max-w-2xl">
            Everything you need to know about your ResumeLab Pro plan. Have another question?{" "}
            <a href="mailto:hello@resumelab.co.ke" className="text-[#EF4444] hover:underline font-medium">Email us</a>.
          </p>
          <div className="bg-white/80 border border-zinc-200">
            {FAQ_ITEMS.map((item, idx) => (
              <div key={item.q} className="px-5 sm:px-6">
                <FAQItem q={item.q} a={item.a} idx={idx} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
